import { BackupFileBrowser } from '@features/backups/components/BackupFileBrowser';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneDotClasses } from '@shared/utils/statusTone';
import { BackupHistoryModal } from '@features/backups/components/BackupHistoryModal';
import { BackupJobLogsViewer } from '@features/backups/components/BackupJobLogsViewer';
import { RestoreModal } from '@features/restores/components/RestoreModal';
import { SelectiveRestoreWizard } from '@features/restores/components/SelectiveRestoreWizard';
import {
  Badge,
  Button,
  Card,
  DataTable,
  TableNumericCell,
  TablePrimaryCell,
} from '@shared/components/ui';
import { useDialog } from '@shared/hooks/useDialog';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { formatBytes, formatRelativeTime } from '@/shared/utils';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import {
  Activity as ActivityIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  Eye,
  ExternalLink,
  FileSearch,
  HardDrive,
  History,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface Agent {
  id: string;
  name: string;
  status: string;
}

interface BackupJob {
  id: string;
  organization_id: string;
  backup_config_id: string;
  backup_config_name: string;
  backup_config_type?: string;
  agent_name: string;
  status: string;
  triggered_by: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  size_bytes: number | null;
  speed_mbps: number | null;
  created_at: string;
  compression_ratio?: number | null;
  space_saved_bytes?: number | null;
  original_size_bytes?: number | null;
  stored_size_bytes?: number | null;
  repo_size_after_bytes?: number | null;
  backup_id?: string | null;
  metadata?: unknown;
  error_message?: string | null;
  is_restorable?: boolean;
  // Progress fields for real-time updates
  progress_percent?: number | null;
  bytes_done?: number | null;
  bytes_total?: number | null;
  current_file?: string | null;
  message?: string | null;
  // Source connection details for PostgreSQL
  source_host?: string;
  source_port?: number;
  source_database?: string;
  source_username?: string;
}

interface BackupJobsResponse {
  jobs: BackupJob[];
  count: number;
  total: number;
  page: number;
  page_size: number;
  timezone: string;
}

function BackupJobStatusBadge({
  status,
  label,
  variant,
  explanation,
}: {
  status: string;
  label: string;
  variant: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  explanation: string;
}) {
  const isExpired = status.toLowerCase() === 'deleted';
  const badge = <Badge variant={variant}>{label}</Badge>;

  if (!isExpired) return badge;

  return (
    <span
      title={explanation}
      aria-label={`${label}. ${explanation}`}
      tabIndex={0}
      className="inline-flex rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
    >
      {badge}
    </span>
  );
}

export default function BackupJobs() {
  const { t, i18n } = useTranslation('backups');
  const { showError } = useDialog();
  const navigate = useNavigate();

  // Helper function to get translated status labels
  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return t('jobs.filters.status.completed');
      case 'recovered':
        return t('jobs.filters.status.recovered');
      case 'failed':
        return t('jobs.filters.status.failed');
      case 'running':
        return t('jobs.filters.status.running');
      case 'reconciling':
        return t('jobs.filters.status.reconciling');
      case 'pending':
        return t('jobs.filters.status.pending');
      case 'deleted':
        return t('jobs.filters.status.deleted');
      case 'quota_exceeded':
        return t('jobs.filters.status.quotaExceeded');
      default:
        return status.toUpperCase();
    }
  };

  // Helper function to get translated trigger labels
  const getTriggerLabel = (trigger: string) => {
    switch (trigger.toLowerCase()) {
      case 'schedule':
        return t('jobs.labels.schedule');
      case 'manual':
        return t('jobs.labels.manual');
      default:
        return trigger.toUpperCase();
    }
  };
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState<BackupJob | null>(null);
  // Use a ref to track the currently selected job ID to avoid stale closures in fetchJobs
  const selectedJobIdRef = useRef<string | null>(null);
  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response (a `[jobs]` dependency would restart the timer each tick).
  const jobsRef = useRef<BackupJob[]>([]);
  const fetchJobsRef = useRef<(background?: boolean) => Promise<void>>(async () => {});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showSelectiveRestore, setShowSelectiveRestore] = useState(false);
  const [restoreFileSelection, setRestoreFileSelection] = useState<{
    backupJobId: string;
    files: string[];
  } | null>(null);
  const selectedFilesForRestore =
    selectedJob && restoreFileSelection?.backupJobId === selectedJob.id
      ? restoreFileSelection.files
      : [];
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [restoreAgentId, setRestoreAgentId] = useState<string>('');

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [restorableFilter, setRestorableFilter] = useState(searchParams.get('restorable') || '');
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agent') || '');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const getToken = useCallback(() => localStorage.getItem('auth_token'), []);

  // Helper to detect VSS-related errors
  const isVSSError = (errorMessage: string | null | undefined): boolean => {
    if (!errorMessage) return false;
    const vssKeywords = ['vss', 'shadow copy', 'volume shadow', 'snapshot', '0x800423'];
    const lowerError = errorMessage.toLowerCase();
    return vssKeywords.some((keyword) => lowerError.includes(keyword));
  };

  // Helper to detect permission-related errors
  const isPermissionError = (errorMessage: string | null | undefined): boolean => {
    if (!errorMessage) return false;
    const permissionKeywords = [
      'access denied',
      'permission denied',
      'unauthorized',
      'forbidden',
      '403',
      'insufficient privileges',
      'not permitted',
      'elevation required',
    ];
    const lowerError = errorMessage.toLowerCase();
    return permissionKeywords.some((keyword) => lowerError.includes(keyword));
  };

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/agents', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch {
      // Silently fail
    }
  }, [getToken]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, restorableFilter, agentFilter]);

  // Keep the selectedJobIdRef in sync with selectedJob state
  useEffect(() => {
    selectedJobIdRef.current = selectedJob?.id ?? null;
  }, [selectedJob]);

  const selectedJobId = selectedJob?.id ?? null;
  const selectedJobStatus = selectedJob?.status ?? null;

  // Live progress while the details modal is open on a running job. Adaptive:
  // 4s base rather than the old fixed 1s, backing off once progress stops
  // changing. The 1s interval alone cost 60 req/min per open modal.
  const trackProgress = useCallback(async () => {
    if (!selectedJobId) return '';
    const response = await api.get<{
      id: string;
      status: string;
      progress_percent: number;
      current_file: string | null;
      bytes_done: number;
      bytes_total: number;
      message: string | null;
    }>(`/backup-jobs/${selectedJobId}/progress`);

    const d = response.data;
    // Preserve progress-specific fields on the selected job for the modal body.
    setSelectedJob((prev) =>
      prev
        ? {
            ...prev,
            progress_percent: d.progress_percent,
            current_file: d.current_file,
            bytes_done: d.bytes_done,
            bytes_total: d.bytes_total,
            message: d.message,
            status: d.status as BackupJob['status'],
          }
        : null
    );
    return `${d.status}:${d.progress_percent}:${d.bytes_done}:${d.message ?? ''}`;
  }, [selectedJobId]);

  // Worst case is a transfer whose progress changes on every tick, which keeps
  // the cadence at the base rate: at 4s that alone was 15 req/min, ~90% of the
  // organization's entire hourly budget. 5s brings the worst case to 12/min
  // while still feeling live; the cadence backs off to 30s the moment progress
  // stops moving, which is the common case for a stalled job.
  useAdaptivePoll({
    enabled:
      showDetailsModal &&
      !!selectedJobId &&
      (selectedJobStatus === 'RUNNING' || selectedJobStatus === 'PENDING'),
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: trackProgress,
    isEqual: (a, b) => a === b,
  });

  const fetchJobs = async (background = false) => {
    try {
      if (!background) setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (restorableFilter) params.append('restorable', restorableFilter);
      if (agentFilter) params.append('agent', agentFilter);

      const response = await api.get<BackupJobsResponse>(`/backup-jobs?${params.toString()}`);
      setJobs(response.data.jobs);
      setTotal(response.data.total || 0);
      setTimezone(response.data.timezone || 'UTC');

      // Update selectedJob if it's still open and has updated data in the response
      // Use the ref to get the current selected job ID to avoid stale closures
      const currentSelectedId = selectedJobIdRef.current;
      if (currentSelectedId) {
        const updatedJob = response.data.jobs.find((j) => j.id === currentSelectedId);
        if (updatedJob) {
          // Preserve progress-specific fields that only the progress endpoint provides
          // to prevent flickering when the jobs list refreshes
          setSelectedJob((prev) =>
            prev
              ? {
                  ...updatedJob,
                  current_file: prev?.current_file ?? updatedJob.current_file,
                  bytes_done: prev?.bytes_done ?? updatedJob.bytes_done,
                  bytes_total: prev?.bytes_total ?? updatedJob.bytes_total,
                  message: prev?.message ?? updatedJob.message,
                }
              : prev
          );
        }
      }
    } catch {
      setError(t('errors:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Keep the poll's refs pointing at the latest values. Assigned on every render
  // rather than in an effect so the first poll tick already sees them.
  jobsRef.current = jobs;
  fetchJobsRef.current = fetchJobs;

  // Auto-refresh while jobs are running. Adaptive: 5s base, backing off to 30s
  // once responses stop changing, and never while the tab is hidden. The old
  // fixed 2s interval cost 27 req/min per idle tab — more than the whole
  // organization's 1000/hour budget.
  //
  // Suspended while the details modal is open: the modal polls the same job's
  // progress, and refreshing the list behind it would double the cost for data
  // the user cannot see.
  useAdaptivePoll({
    enabled:
      !showDetailsModal && jobs.some((job) => job.status === 'RUNNING' || job.status === 'PENDING'),
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      await fetchJobsRef.current(true);
      return jobsRef.current
        .map((j) => `${j.id}:${j.status}:${j.progress_percent ?? ''}`)
        .join('|');
    },
    isEqual: (a, b) => a === b,
  });
  // Validate agent is online before opening restore modal
  const validateAndOpenRestore = async (job: BackupJob) => {
    // Fetch backup config to verify agent is online
    try {
      const response = await api.get(`/backups/configs/${job.backup_config_id}`);
      const config = response.data;

      // Check if agent is linked to this backup config
      if (!config.agent_id) {
        await showError(
          t('snapshots.errors.noAgentLinked', 'Nenhum agente vinculado'),
          t(
            'snapshots.errors.agentRequired',
            'Esta configuração de backup não possui um agente vinculado.'
          )
        );
        return;
      }

      // Check agent status - must be ONLINE for restore
      const agentStatus = config.agent_status?.toUpperCase();
      if (agentStatus !== 'ONLINE') {
        await showError(
          t('snapshots.errors.agentOffline', 'Agente indisponível'),
          t(
            'snapshots.errors.agentOfflineMessage',
            'O agente "{{agentName}}" não está online. A restauração só pode ser executada quando houver conexão.',
            {
              agentName: config.agent_name || config.agent_id,
            }
          )
        );
        return;
      }

      // All checks passed - open restore modal with agent_id
      setSelectedJob(job);
      setRestoreAgentId(config.agent_id);
      setShowFileBrowser(false);
      setShowSelectiveRestore(true);
    } catch {
      await showError(
        t('snapshots.errors.loadConfigFailed', 'Erro ao verificar configuração'),
        t(
          'snapshots.errors.tryAgain',
          'Não foi possível verificar o status do agente. Tente novamente.'
        )
      );
    }
  };

  return (
    <>
      <div className="space-y-8">
        <div className="flex flex-col justify-between gap-4 md:flex-col md:items-start xl:flex-row xl:items-center">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('jobs.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
              {t('jobs.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-2">
            <Badge variant="info" className="h-auto min-h-6 whitespace-nowrap">
              {t('jobs.labels.timezone')} {timezone}
            </Badge>
            <Badge variant="neutral" className="h-auto min-h-6 whitespace-nowrap">
              {t('jobs.labels.totalJobs', { count: total })}
            </Badge>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(200px,1fr)_repeat(3,minmax(0,auto))_auto]">
            <div className="relative min-w-0 md:col-span-2 xl:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark w-4 h-4" />
              <input
                type="text"
                placeholder={t('jobs.searchPlaceholder')}
                aria-label={t('jobs.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full min-h-11 pl-10 pr-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm focus:ring-2 focus:ring-brand-500 transition-all"
              />
            </div>
            <select
              aria-label={t('jobs.filters.statusLabel')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="!min-h-11 w-full min-w-0 px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('jobs.filters.allStatuses')}</option>
              <option value="COMPLETED">{t('jobs.filters.status.completed')}</option>
              <option value="RECOVERED">{t('jobs.filters.status.recovered')}</option>
              <option value="FAILED">{t('jobs.filters.status.failed')}</option>
              <option value="RUNNING">{t('jobs.filters.status.running')}</option>
              <option value="STALE">{t('jobs.filters.status.reconciling')}</option>
              <option value="PENDING">{t('jobs.filters.status.pending')}</option>
              <option value="DELETED">{t('jobs.filters.status.deleted')}</option>
              <option value="QUOTA_EXCEEDED">{t('jobs.filters.status.quotaExceeded')}</option>
            </select>
            <select
              aria-label={t('jobs.filters.allTypes')}
              value={restorableFilter}
              onChange={(e) => setRestorableFilter(e.target.value)}
              className="!min-h-11 w-full min-w-0 px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('jobs.filters.allTypes')}</option>
              <option value="true">{t('jobs.filters.type.restorable')}</option>
              <option value="false">{t('jobs.filters.type.pruned')}</option>
            </select>
            <select
              aria-label={t('jobs.filters.allAgents')}
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="!min-h-11 w-full min-w-0 px-4 py-2 bg-surface-2-light dark:bg-surface-2-dark border-none rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">{t('jobs.filters.allAgents')}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.name}>
                  {a.name}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setRestorableFilter('');
                setAgentFilter('');
              }}
              className="!min-h-11 w-full whitespace-nowrap xl:w-auto"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> {t('jobs.actions.reset')}
            </Button>
          </div>
        </Card>

        {loading ? (
          <div className="py-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('common:actions.loading')}
            </p>
          </div>
        ) : jobs.length === 0 ? (
          <Card className="py-20 text-center">
            <Database className="w-16 h-16 text-surface-border-light dark:text-surface-border-dark mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('jobs.empty.title')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark">
              {t('jobs.empty.message')}
            </p>
          </Card>
        ) : (
          <>
            {/* Compact card layout — phones and small tablets */}
            <div className="grid grid-cols-1 gap-2 lg:hidden">
              {jobs.map((job) => (
                <Card key={job.id} className="p-0 overflow-hidden group">
                  <div className="grid grid-cols-2 gap-4 p-4">
                    <div className="col-span-2 flex items-center space-x-3">
                      <div
                        className={`w-1.5 h-12 rounded-full ${toneDotClasses[getStatusTone(job.status)]}`}
                      />
                      <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
                        <HardDrive size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-black text-slate-900 dark:text-white uppercase tracking-tight truncate">
                            {job.backup_config_name}
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/backups/configs/${job.backup_config_id}/snapshots`);
                            }}
                            aria-label={t('jobs.viewSnapshots')}
                            className="!min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-border-light dark:hover:bg-surface-border-dark transition-colors opacity-100 lg:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
                            title={t('jobs.viewSnapshots')}
                          >
                            <Eye
                              size={14}
                              className="text-text-subtle-light hover:text-brand-600 dark:text-text-subtle-dark dark:hover:text-brand-400"
                            />
                          </button>
                          <BackupJobStatusBadge
                            status={job.status}
                            label={getStatusLabel(job.status)}
                            variant={toneToBadgeVariant[getStatusTone(job.status)]}
                            explanation={t('jobs.filters.status.deletedExplanation')}
                          />
                        </div>
                        <div className="mt-1 flex items-center space-x-3 text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                          <span className="flex items-center">
                            <ActivityIcon size={10} className="mr-1" /> {job.agent_name}
                          </span>
                          <span
                            className="flex items-center"
                            title={new Date(job.created_at).toLocaleString()}
                          >
                            <Clock size={10} className="mr-1" />{' '}
                            {formatRelativeTime(job.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {(job.status === 'RUNNING' || job.status === 'PENDING') && (
                      <div className="col-span-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-text-muted-light dark:text-text-muted-dark">
                            {job.message || t('jobs.modal.processing')}
                          </span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {Math.round(job.progress_percent || 0)}%
                          </span>
                        </div>
                        <div className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-1.5">
                          <div
                            className="bg-state-info-light dark:bg-state-info-dark h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${job.progress_percent || 0}%` }}
                          />
                        </div>
                        {(job.bytes_done || job.bytes_total) && (
                          <div className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                            {formatBytes(job.bytes_done)} / {formatBytes(job.bytes_total)}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                        {job.original_size_bytes
                          ? t('jobs.labels.original')
                          : t('jobs.labels.size')}
                      </p>
                      <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                        {formatBytes(job.original_size_bytes || job.size_bytes)}
                      </p>
                      {job.stored_size_bytes != null && job.original_size_bytes != null && (
                        <p className="text-[10px] text-state-ok-light dark:text-state-ok-dark font-bold mt-0.5">
                          {t('jobs.labels.stored')}: {formatBytes(job.stored_size_bytes)}
                          {job.compression_ratio != null &&
                            ` (${(job.compression_ratio * 100).toFixed(0)}% ${t('jobs.labels.saved')})`}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
                        {t('jobs.labels.speed')}
                      </p>
                      <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                        {job.speed_mbps?.toFixed(1) || '0'} MB/s
                      </p>
                    </div>
                    <div className="col-span-2 flex items-center space-x-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="!min-h-11"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowDetailsModal(true);
                        }}
                      >
                        {t('jobs.actions.details')}
                      </Button>
                      {job.status === 'COMPLETED' &&
                        (job.is_restorable === undefined || job.is_restorable === true) && (
                          <Button
                            size="sm"
                            className="!min-h-11"
                            onClick={() => validateAndOpenRestore(job)}
                          >
                            {t('jobs.actions.restore')}
                          </Button>
                        )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Tabular layout — desktop. Column widths come from content, so
                localized labels are never clipped; the identity column absorbs
                the remaining width. */}
            <div className="hidden lg:block">
              <DataTable
                caption={t('jobs.title')}
                rows={jobs}
                rowKey={(job) => job.id}
                rowAccent={(job) => toneDotClasses[getStatusTone(job.status)]}
                columns={[
                  {
                    key: 'job',
                    header: t('jobs.title'),
                    width: 'w-full',
                    cell: (job) => (
                      <TablePrimaryCell
                        title={job.backup_config_name}
                        trailing={
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/backups/configs/${job.backup_config_id}/snapshots`);
                            }}
                            aria-label={t('jobs.viewSnapshots')}
                            className="!min-h-11 min-w-11 rounded-lg p-2 opacity-100 lg:opacity-0 transition-colors group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                            title={t('jobs.viewSnapshots')}
                          >
                            <Eye
                              size={14}
                              className="text-text-subtle-light hover:text-brand-600 dark:text-text-subtle-dark dark:hover:text-brand-400"
                            />
                          </button>
                        }
                        meta={
                          <>
                            <span className="flex items-center gap-1">
                              <ActivityIcon size={12} /> {job.agent_name}
                            </span>
                            <span
                              className="flex items-center gap-1"
                              title={new Intl.DateTimeFormat(i18n.language, {
                                dateStyle: 'short',
                                timeStyle: 'medium',
                              }).format(new Date(job.created_at))}
                            >
                              <Clock size={12} /> {formatRelativeTime(job.created_at)}
                            </span>
                          </>
                        }
                      />
                    ),
                  },
                  {
                    // Own column so every badge shares one vertical edge,
                    // regardless of how long the job name is.
                    key: 'status',
                    header: t('jobs.filters.statusLabel'),
                    align: 'left',
                    width: 'w-[132px]',
                    cell: (job) => (
                      <BackupJobStatusBadge
                        status={job.status}
                        label={getStatusLabel(job.status)}
                        variant={toneToBadgeVariant[getStatusTone(job.status)]}
                        explanation={t('jobs.filters.status.deletedExplanation')}
                      />
                    ),
                  },
                  {
                    key: 'size',
                    header: t('jobs.labels.size'),
                    align: 'right',
                    cell: (job) => (
                      <TableNumericCell
                        value={formatBytes(job.original_size_bytes || job.size_bytes)}
                        hint={
                          job.stored_size_bytes != null && job.original_size_bytes != null
                            ? `${t('jobs.labels.stored')}: ${formatBytes(job.stored_size_bytes)}`
                            : undefined
                        }
                      />
                    ),
                  },
                  {
                    key: 'speed',
                    header: t('jobs.labels.speed'),
                    align: 'right',
                    cell: (job) => (
                      <TableNumericCell value={(job.speed_mbps ?? 0).toFixed(1)} unit="MB/s" />
                    ),
                  },
                  {
                    key: 'duration',
                    header: t('jobs.labels.duration'),
                    align: 'right',
                    cell: (job) => (
                      <TableNumericCell
                        value={
                          job.duration_seconds != null
                            ? t('jobs.labels.durationSeconds', { count: job.duration_seconds })
                            : '—'
                        }
                      />
                    ),
                  },
                  {
                    key: 'trigger',
                    header: t('jobs.labels.trigger'),
                    align: 'center',
                    hideBelow: 'xl',
                    cell: (job) => (
                      <span className="whitespace-nowrap text-xs font-bold uppercase tracking-wide text-text-muted-light dark:text-text-muted-dark">
                        {getTriggerLabel(job.triggered_by)}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    align: 'right',
                    // Fixed track: keeps every row's buttons on the same edge
                    // whether or not the row offers "Restaurar".
                    width: 'w-[210px]',
                    cell: (job) => (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="!min-h-11"
                          onClick={() => {
                            setSelectedJob(job);
                            setShowDetailsModal(true);
                          }}
                        >
                          {t('jobs.actions.details')}
                        </Button>
                        {job.status === 'COMPLETED' &&
                          (job.is_restorable === undefined || job.is_restorable === true) && (
                            <Button
                              size="sm"
                              className="!min-h-11"
                              onClick={() => validateAndOpenRestore(job)}
                            >
                              {t('jobs.actions.restore')}
                            </Button>
                          )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between pt-4">
          <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
            {t('jobs.pagination.showing', { from: jobs.length, total })}
          </p>
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              className="!min-h-11"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft size={16} className="mr-1" /> {t('jobs.pagination.prev')}
            </Button>
            <div
              aria-current="page"
              className="!min-h-11 min-w-11 flex items-center justify-center px-4 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-xs font-black"
            >
              {page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="!min-h-11"
              disabled={jobs.length < pageSize}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('jobs.pagination.next')} <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Details Modal Overhaul */}
      {showDetailsModal && selectedJob && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="backup-job-detail-title"
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
        >
          <Card className="my-auto w-full max-w-2xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto animate-in zoom-in-95 duration-300 shadow-elevated">
            <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-accent-soft-light dark:bg-accent-soft-dark rounded-2xl flex items-center justify-center text-brand-600 dark:text-brand-400">
                    <Database size={28} />
                  </div>
                  <div>
                    <h2
                      id="backup-job-detail-title"
                      className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                    >
                      {selectedJob.backup_config_name}
                    </h2>
                    <BackupJobStatusBadge
                      status={selectedJob.status}
                      label={getStatusLabel(selectedJob.status)}
                      variant={toneToBadgeVariant[getStatusTone(selectedJob.status)]}
                      explanation={t('jobs.filters.status.deletedExplanation')}
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowErrorDetails(false);
                  }}
                  aria-label={t('common:actions.close')}
                  title={t('common:actions.close')}
                  className="!min-h-11 min-w-11 flex items-center justify-center p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl transition-colors"
                >
                  <X size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
                </button>
              </div>

              {/* Progress section for running jobs */}
              {(selectedJob.status === 'RUNNING' || selectedJob.status === 'PENDING') && (
                <div className="p-6 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black text-state-info-light dark:text-state-info-dark uppercase tracking-[0.2em] mb-1">
                        {t('jobs.modal.progress')}
                      </p>
                      <p className="text-sm font-medium text-state-info-light dark:text-state-info-dark">
                        {selectedJob.message || t('jobs.modal.processing')}
                      </p>
                    </div>
                    <span className="text-2xl font-black text-state-info-light dark:text-state-info-dark">
                      {Math.round(selectedJob.progress_percent || 0)}%
                    </span>
                  </div>
                  <div className="w-full bg-state-info-bg-light dark:bg-state-info-bg-dark rounded-full h-3">
                    <div
                      className="bg-state-info-light dark:bg-state-info-dark h-3 rounded-full transition-all duration-500"
                      style={{ width: `${selectedJob.progress_percent || 0}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-state-info-light/70 dark:text-state-info-dark/70">
                    <span>{selectedJob.current_file || ''}</span>
                    <span>
                      {selectedJob.bytes_done || selectedJob.bytes_total
                        ? `${formatBytes(selectedJob.bytes_done)} / ${formatBytes(selectedJob.bytes_total)}`
                        : ''}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 sm:gap-y-8 gap-x-6 sm:gap-x-12">
                <div className="sm:col-span-2">
                  <DetailItem label={t('jobs.labels.jobId')} value={selectedJob.id} mono truncate />
                </div>
                <div className="sm:col-span-2">
                  <DetailItem
                    label={t('jobs.labels.snapshotId')}
                    value={selectedJob.backup_id || '-'}
                    mono
                    truncate
                  />
                </div>
                <DetailItem
                  label={t('jobs.labels.startedAt')}
                  value={
                    selectedJob.started_at
                      ? new Intl.DateTimeFormat(i18n.language, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(new Date(selectedJob.started_at))
                      : '-'
                  }
                />
                <DetailItem
                  label={t('jobs.labels.completedAt')}
                  value={
                    selectedJob.completed_at
                      ? new Intl.DateTimeFormat(i18n.language, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(new Date(selectedJob.completed_at))
                      : '-'
                  }
                />
                <DetailItem
                  label={t('jobs.labels.compression')}
                  value={
                    selectedJob.compression_ratio
                      ? `${selectedJob.compression_ratio.toFixed(1)}% ${t('jobs.labels.reduction')}`
                      : '-'
                  }
                />
                <DetailItem
                  label={t('jobs.labels.avgSpeed')}
                  value={selectedJob.speed_mbps ? `${selectedJob.speed_mbps.toFixed(2)} Mbps` : '-'}
                />
                <DetailItem
                  label={t('jobs.labels.triggeredBy')}
                  value={getTriggerLabel(selectedJob.triggered_by)}
                />
              </div>

              {selectedJob.status === 'FAILED' && (
                <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-2xl">
                  {isVSSError(selectedJob.error_message) ? (
                    <>
                      <p className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-[0.2em] mb-2">
                        {t('jobs.modal.vssError')}
                      </p>
                      <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark leading-relaxed mb-3">
                        {t('jobs.modal.vssErrorDescription')}
                      </p>
                      <button
                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                        className="text-xs text-state-danger-light dark:text-state-danger-dark underline hover:no-underline font-medium"
                      >
                        {showErrorDetails
                          ? t('jobs.modal.hideDetails')
                          : t('jobs.modal.showDetails')}
                      </button>
                      {showErrorDetails && (
                        <div className="mt-3 p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-lg">
                          <p className="text-xs font-mono text-state-danger-light dark:text-state-danger-dark break-all">
                            {selectedJob.error_message}
                          </p>
                        </div>
                      )}
                    </>
                  ) : isPermissionError(selectedJob.error_message) ? (
                    <>
                      <p className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-[0.2em] mb-2">
                        {t('jobs.modal.permissionError')}
                      </p>
                      <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark leading-relaxed mb-3">
                        {t('jobs.modal.permissionErrorDescription')}
                      </p>
                      <button
                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                        className="text-xs text-state-danger-light dark:text-state-danger-dark underline hover:no-underline font-medium"
                      >
                        {showErrorDetails
                          ? t('jobs.modal.hideDetails')
                          : t('jobs.modal.showDetails')}
                      </button>
                      {showErrorDetails && (
                        <div className="mt-3 p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-lg">
                          <p className="text-xs font-mono text-state-danger-light dark:text-state-danger-dark break-all">
                            {selectedJob.error_message}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-[10px] font-black text-state-danger-light dark:text-state-danger-dark uppercase tracking-[0.2em] mb-1">
                        {t('jobs.modal.errorReport')}
                      </p>
                      <p className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark leading-relaxed">
                        {selectedJob.error_message}
                      </p>
                    </>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowLogsModal(true);
                  }}
                >
                  <ActivityIcon size={16} className="mr-2" /> {t('jobs.actions.logs')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowHistoryModal(true);
                  }}
                >
                  <History size={16} className="mr-2" /> {t('jobs.actions.history')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-1"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowFileBrowser(true);
                  }}
                >
                  <FileSearch size={16} className="mr-2" /> {t('jobs.actions.files')}
                </Button>
                {selectedJob.status === 'COMPLETED' &&
                  (selectedJob.is_restorable === undefined ||
                    selectedJob.is_restorable === true) && (
                    <Button
                      variant="primary"
                      size="md"
                      className="flex-1"
                      onClick={() => {
                        setShowDetailsModal(false);
                        validateAndOpenRestore(selectedJob);
                      }}
                    >
                      <ExternalLink size={16} className="mr-2" /> {t('jobs.actions.restore')}
                    </Button>
                  )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Existing Functional Modals */}
      {showRestoreModal && selectedJob && (
        <RestoreModal
          isOpen={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
          backupJobId={selectedJob.id}
          backupConfigName={selectedJob.backup_config_name}
          backupType={selectedJob.backup_config_type as 'FILES' | 'POSTGRESQL' | undefined}
          sourceHost={selectedJob.source_host}
          sourcePort={selectedJob.source_port}
          sourceDatabase={selectedJob.source_database}
          sourceUsername={selectedJob.source_username}
          onSuccess={() => {
            setShowRestoreModal(false);
            fetchJobs();
          }}
          selectedFiles={selectedFilesForRestore}
        />
      )}
      {showFileBrowser && selectedJob && (
        <BackupFileBrowser
          isOpen={showFileBrowser}
          onClose={() => setShowFileBrowser(false)}
          backupJobId={selectedJob.id}
          backupConfigName={selectedJob.backup_config_name}
          initialSelectedFiles={selectedFilesForRestore}
          onSelectFiles={async (files) => {
            setRestoreFileSelection({ backupJobId: selectedJob.id, files });
            // Validate agent status before opening restore
            await validateAndOpenRestore(selectedJob);
          }}
        />
      )}
      {showHistoryModal && selectedJob && (
        <BackupHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          backupConfigId={selectedJob.backup_config_id}
          backupConfigName={selectedJob.backup_config_name}
          onSelectBackup={async (id) => {
            const res = await api.get<BackupJob>(`/backup-jobs/${id}`);
            const selectedHistoryJob = res.data;
            setShowHistoryModal(false);
            // Validate agent status before opening restore
            await validateAndOpenRestore(selectedHistoryJob);
          }}
        />
      )}
      {showLogsModal && selectedJob && (
        <BackupJobLogsViewer
          isOpen={showLogsModal}
          onClose={() => setShowLogsModal(false)}
          backupJobId={selectedJob.id}
          backupConfigName={selectedJob.backup_config_name}
        />
      )}

      {/* Selective Restore Wizard - New WebSocket-based restore */}
      {showSelectiveRestore && selectedJob && (
        <SelectiveRestoreWizard
          isOpen={showSelectiveRestore}
          onClose={() => {
            setShowSelectiveRestore(false);
            setRestoreAgentId('');
          }}
          backupJobId={selectedJob.id}
          backupConfigName={selectedJob.backup_config_name}
          backupConfigType={selectedJob.backup_config_type as 'FILES' | 'POSTGRESQL' | undefined}
          sourceHost={selectedJob.source_host}
          sourcePort={selectedJob.source_port}
          sourceDatabase={selectedJob.source_database}
          sourceUsername={selectedJob.source_username}
          backupJobSnapshotId={selectedJob.backup_id}
          agentId={restoreAgentId}
          initialSelectedFiles={selectedFilesForRestore}
          onSelectedFilesChange={(files) =>
            setRestoreFileSelection({ backupJobId: selectedJob.id, files })
          }
          onSuccess={() => {
            fetchJobs();
            setRestoreFileSelection(null);
          }}
        />
      )}
    </>
  );
}

function DetailItem({
  label,
  value,
  mono,
  truncate,
}: {
  label: string;
  value: string;
  mono?: boolean;
  truncate?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em] mb-1.5">
        {label}
      </p>
      <p
        className={`text-sm font-bold text-slate-900 dark:text-white ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : ''}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
